import { useMemo } from 'react';
import type {
  CommentMatch as CommentMatchType,
  TranscriptionMatch as TranscriptionMatchType,
  VisualMatch as VisualMatchType,
} from '@frameio/graphql-client/generated';
// import type { AssetResultMatch } from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import { Box, Text } from '@frameio/vapor';
import type {
  AssetResultMatch,
  OnMatchClickHandler,
} from '../../../../../shared/types';
import { InfoListContainer } from '../shared';
import {
  ClipMatch,
  CommentMatch,
  FieldMatch,
  TranscriptionMatch,
} from './components';
import TEXT from './generated/TEXT/ItemDetailMatchesQS';

TEXT.DEFINE({
  /** Match count display with pluralization */
  matchCount: ({ count }: { count: number }) =>
    `${count} ${plural(count, 'matches', 'match')}`,
});

type ItemDetailMatchesProps = {
  assetId: string;
  matches: AssetResultMatch[];
  onMatchClick?: OnMatchClickHandler;
};

export const ItemDetailMatches = ({
  assetId,
  matches,
  onMatchClick,
}: ItemDetailMatchesProps) => {
  const { visualMatches, fieldMatches, transcriptionMatches, commentMatches } =
    useMemo(() => {
      return matches.reduce<{
        commentMatches: CommentMatchType[];
        transcriptionMatches: TranscriptionMatchType[];
        visualMatches: VisualMatchType[];
        fieldMatches: AssetResultMatch[];
      }>(
        (acc, match) => {
          switch (match.__typename) {
            case 'VisualMatch':
              acc.visualMatches.push(match as VisualMatchType);
              break;
            case 'TranscriptionMatch':
              acc.transcriptionMatches.push(match as TranscriptionMatchType);
              break;
            case 'CommentMatch':
              acc.commentMatches.push(match as CommentMatchType);
              break;
            default:
              acc.fieldMatches.push(match);
          }
          return acc;
        },
        {
          commentMatches: [] as CommentMatchType[],
          transcriptionMatches: [] as TranscriptionMatchType[],
          visualMatches: [] as VisualMatchType[],
          fieldMatches: [] as Exclude<AssetResultMatch, VisualMatchType>[],
        }
      );
    }, [matches]);

  const hasVisualMatches = visualMatches.length > 0;
  const hasFieldMatches = fieldMatches.length > 0;
  const hasTranscriptionMatches = transcriptionMatches.length > 0;
  const hasCommentMatches = commentMatches.length > 0;

  const totalMatchCount = useMemo(() => {
    const visualMatchCount = visualMatches.reduce(
      (acc: number, visualMatch) => {
        return acc + (visualMatch.clips?.length ?? 0);
      },
      0
    );
    const transcriptionMatchCount = transcriptionMatches.reduce(
      (acc: number, transcriptionMatch) => {
        return (
          acc +
          (transcriptionMatch.matches?.nodes?.flatMap((node) => node.snippets)
            ?.length ?? 0)
        );
      },
      0
    );
    const commentMatchCount = commentMatches.length;

    return (
      fieldMatches.length +
      visualMatchCount +
      transcriptionMatchCount +
      commentMatchCount
    );
  }, [fieldMatches, visualMatches, transcriptionMatches, commentMatches]);

  return (
    <Box display="flex" flexDirection="column" gap={8}>
      {totalMatchCount > 0 && (
        <Text color="tertiary" variant="secondary">
          {TEXT.matchCount({ count: totalMatchCount })}
        </Text>
      )}

      {hasFieldMatches && (
        <InfoListContainer>
          {fieldMatches.map((match, index) => {
            const matchType = match?.__typename as NonNullable<
              AssetResultMatch['__typename']
            >;

            return (
              <FieldMatch
                key={`${matchType}-${index}`}
                assetId={assetId}
                match={match}
                onMatchClick={onMatchClick}
              />
            );
          })}
        </InfoListContainer>
      )}

      {hasVisualMatches && (
        <ClipMatch
          assetId={assetId}
          onMatchClick={onMatchClick}
          visualMatches={visualMatches}
        />
      )}

      {hasTranscriptionMatches &&
        transcriptionMatches.map((transcriptionMatch, index) => (
          <TranscriptionMatch
            key={`transcript-match-${index}`}
            assetId={assetId}
            onMatchClick={onMatchClick}
            transcriptionMatch={transcriptionMatch}
          />
        ))}

      {hasCommentMatches && (
        <CommentMatch
          assetId={assetId}
          commentMatches={commentMatches}
          onMatchClick={onMatchClick}
        />
      )}
    </Box>
  );
};
